import { Fragment, type ReactNode } from "react";
import { Link } from "react-router-dom";

import { useCollectionStructure } from "../api/documents";
import { collectionPath, useCollections } from "../api/queries";
import type { Doc } from "../api/types";
import { ancestorsOf } from "./tree";

/**
 * Collection → ancestor documents → this document (note 03 §4). The ancestors
 * come out of the collection's navigation tree, which the sidebar has usually
 * already fetched, so the breadcrumb costs nothing on a normal navigation.
 */
export function DocumentBreadcrumb({ document }: { document: Doc }): ReactNode {
  const collections = useCollections();
  const collectionId = document.collectionId ?? undefined;
  const structure = useCollectionStructure(collectionId, collectionId !== undefined);
  const collection = (collections.data ?? []).find((entry) => entry.id === collectionId);
  const ancestors = ancestorsOf(structure.data ?? [], document.id);

  return (
    <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1 text-sm text-muted">
      {collection === undefined ? (
        <Link to="/drafts" className="hover:underline">
          Drafts
        </Link>
      ) : (
        <Link to={collectionPath(collection)} className="truncate hover:underline">
          {collection.name}
        </Link>
      )}
      {ancestors.map((ancestor) => (
        <Fragment key={ancestor.id}>
          <span aria-hidden="true">/</span>
          <Link to={ancestor.url} className="truncate hover:underline">
            {ancestor.title === "" ? "Untitled" : ancestor.title}
          </Link>
        </Fragment>
      ))}
    </nav>
  );
}
